import { trackEvent, INTEGRATIONS } from '@/config/integrations';

// Типы событий для отслеживания
export type TrackingEvent = 
  | 'social_click'
  | 'product_view' 
  | 'product_action'
  | 'contact_action'
  | 'service_action';

// Отслеживание кликов по соцсетям
export const trackSocialClick = (platform: string, source: string = 'unknown', productTitle?: string) => {
  console.log(`🔗 Social click: ${platform} from ${source}`);
  
  // Яндекс.Метрика
  trackEvent('social_click', {
    platform,
    source,
    timestamp: Date.now()
  });

  // Сохранение в БД
  saveAnalyticsEvent('social_click', platform, 'social', {
    platform,
    source,
    product_title: productTitle
  });

  // Отправка уведомления в VK (если включено)
  if (INTEGRATIONS.VK.ENABLED) {
    const actionText = platform === 'VK' ? 'нажали "ВКонтакте"' : 
                      platform === 'WhatsApp' ? 'нажали "WhatsApp"' : 
                      `нажали "${platform}"`;
    
    const sourceText = source.includes('product_') ? 
      `со страницы картины "${productTitle || 'неизвестная картина'}"` :
      source === 'footer' ? 'из футера сайта' :
      source === 'header' ? 'из шапки сайта' :
      `из раздела: ${source}`;
    
    sendVKNotification(`Посетитель ${actionText} ${sourceText}`);
  }
};

// Отслеживание просмотров товаров
export const trackProductView = (productId: string, productTitle: string) => {
  console.log(`👁️ Product view: ${productTitle} (${productId})`);
  
  trackEvent('product_view', {
    product_id: productId,
    product_title: productTitle,
    timestamp: Date.now()
  });

  // Сохранение в БД
  saveAnalyticsEvent('product_view', productId, 'product', {
    product_title: productTitle
  });

  // Сохранение в localStorage для статистики (fallback)
  const views = JSON.parse(localStorage.getItem('product_views') || '{}');
  views[productId] = (views[productId] || 0) + 1;
  localStorage.setItem('product_views', JSON.stringify(views));
};

// Отслеживание действий в товарах
export const trackProductAction = (
  productId: string, 
  action: string, 
  productTitle?: string
) => {
  console.log(`🛒 Product action: ${action} for ${productTitle} (${productId})`);
  
  trackEvent('product_action', {
    product_id: productId,
    product_title: productTitle,
    action,
    timestamp: Date.now()
  });

  // Сохранение в БД
  saveAnalyticsEvent('product_action', productId, 'product', {
    product_title: productTitle,
    action: action
  });

  // Сохранение кликов по кнопкам в товарах (fallback)
  const actions = JSON.parse(localStorage.getItem('product_actions') || '{}');
  const key = `${productId}_${action}`;
  actions[key] = (actions[key] || 0) + 1;
  localStorage.setItem('product_actions', JSON.stringify(actions));
};

// Отслеживание действий с услугами
export const trackServiceAction = (
  serviceId: string,
  serviceTitle: string,
  action: string = 'order_click'
) => {
  console.log(`🛎️ Service action: ${action} for ${serviceTitle} (${serviceId})`);
  
  trackEvent('service_action', {
    service_id: serviceId,
    service_title: serviceTitle,
    action,
    timestamp: Date.now()
  });

  // Сохранение в БД
  saveAnalyticsEvent('service_action', serviceId, 'service', {
    service_title: serviceTitle,
    action: action
  });

  // Отправка уведомления в VK (если включено)
  if (INTEGRATIONS.VK.ENABLED) {
    sendVKNotification(`Посетитель нажал "${action}" для услуги "${serviceTitle}"`);
  }
};

// Сохранение события в БД через новый API
const saveAnalyticsEvent = async (eventType: string, targetId: string, targetType: string, metadata: any = {}) => {
  try {
    await fetch('https://w-wagner.art/get-analytics.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event_type: eventType,
        target_id: targetId,
        target_type: targetType,
        metadata: metadata
      })
    });
  } catch (error) {
    console.error('❌ Error saving analytics event:', error);
  }
};

// Отправка уведомления в VK
const sendVKNotification = async (message: string) => {
  try {
    const vkConfig = INTEGRATIONS.VK;
    if (!vkConfig.ENABLED || !vkConfig.ACCESS_TOKEN) return;

    console.log(`🔗 Sending VK notification to ${vkConfig.USER_IDS.length} users`);

    // Отправка сообщения всем пользователям VK по очереди
    for (const userId of vkConfig.USER_IDS) {
      try {
        const response = await fetch(`${vkConfig.BASE_URL}/messages.send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            access_token: vkConfig.ACCESS_TOKEN,
            v: vkConfig.API_VERSION,
            user_id: userId,
            message: `🟢🟢🟢🟢🟢\n🎨 Уведомление с сайта w-wagner.art\n\n${message}\n\n🟡🟡🟡🟡🟡`,
            random_id: Math.floor(Math.random() * 1000000).toString()
          })
        });

        const result = await response.json();
        
        if (response.ok && result.response) {
          console.log(`✅ VK notification sent to user ${userId}`);
        } else {
          console.error(`❌ Failed to send VK notification to user ${userId}:`, result.error);
        }

        // Небольшая задержка между отправками
        await new Promise(resolve => setTimeout(resolve, 1000));
        
      } catch (userError) {
        console.error(`❌ Error sending VK notification to user ${userId}:`, userError);
      }
    }
  } catch (error) {
    console.error('❌ Error sending VK notification:', error);
  }
};

// Получение статистики просмотров
export const getProductViewStats = () => {
  return JSON.parse(localStorage.getItem('product_views') || '{}');
};

// Получение статистики действий
export const getProductActionStats = () => {
  return JSON.parse(localStorage.getItem('product_actions') || '{}');
};

// Компонент для отображения статистики (для админки)
export const getAnalyticsStats = () => {
  const views = getProductViewStats();
  const actions = getProductActionStats();
  
  return {
    totalViews: Object.values(views).reduce((sum: number, count: number) => sum + count, 0),
    totalActions: Object.values(actions).reduce((sum: number, count: number) => sum + count, 0),
    topProducts: Object.entries(views)
      .sort(([,a], [,b]) => (b as number) - (a as number))
      .slice(0, 5),
    topActions: Object.entries(actions)
      .sort(([,a], [,b]) => (b as number) - (a as number))
      .slice(0, 10)
  };
};